import { MessagingTopology } from '@internal/open-catalog';
import { Box, createStyles, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, makeStyles, Switch, Theme, Typography } from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SnoozeIcon from '@material-ui/icons/Snooze';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { MessagingQueueIcon } from '../../../../../../components/icons';
import React, { ChangeEvent, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../../../components/accordion/accordion.styled';
import { buildAccessRequestsPreset, buildRacPreset, buildSchedulerPreset, buildSgdpPreset, buildSmartTalkPreset, buildStreamQueue, removeStreamQueue, isAccessRequestsEnabled, isDelayedQueueFeatureEnabled, isRacPresetEnabled, isSchedulerPresetEnabled, isSgdpPresetEnabled, isSmartTalkPresetEnabled, isStreamQueueEnabled, removeAccessRequestsPreset, removeRacPreset, removeSchedulerPreset, removeSgdpPreset, removeSmartTalkPreset, buildDelayedQueue, removeDelayedQueue } from './topoloy.preset';

const useMainPanelStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& > p': {
        textAlign: 'center'
      }
    },
    list: {
      width: '100%',
      padding: 0,
      '& li': {
        padding: 0,
        '&:hover': {
          backgroundColor: '#f2f2f2'
        },
        '& div[class|="MuiListItem-root"]': {
          paddingLeft: theme.spacing(1),
        },
        '& div[class|="MuiListItemIcon-root"]': {
          minWidth: 48,
        },
        '& p': {
          textWrap: 'auto',
          paddingRight: theme.spacing(2)
        }
      }
    }
  })
);

export function MainToolbar(props: {
  topology: MessagingTopology,
  smartTalkApplicationId?: string,
  onTopologyChange: (newNodeName?: string) => void
}) {

  const { topology, smartTalkApplicationId, onTopologyChange } = props;

  const classes = useMainPanelStyles();

  const [expanded, setExpanded] = useState<string>('advanced');

  const [delayedChannel, setDelayedChannel] = useState(isDelayedQueueFeatureEnabled(topology));

  const [streamChannel, setStreamChannel] = useState(isStreamQueueEnabled(topology));
  
  const [racPreset, setRacPreset] = useState(isRacPresetEnabled(topology));
  const [sgdpPreset, setSgdpPreset] = useState(isSgdpPresetEnabled(topology));
  const [smartTalkPreset, setSmartTalkPreset] = useState(isSmartTalkPresetEnabled(topology));

  const [schedulerPreset, setSchedulerPreset] = useState(isSchedulerPresetEnabled(topology));
  const [accessRequestsPreset, setAccessRequestsPreset] = useState(isAccessRequestsEnabled(topology));

  const handleAccordionStateChange = (panel: any) => (_event: ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleDelayedChannelChange = () => {
    const enabled = !delayedChannel;
    if (enabled) {
      buildDelayedQueue(topology);
    }
    else {
      removeDelayedQueue(topology);
    }
    onTopologyChange();
    setDelayedChannel(enabled);
  };

  const handleStreamChannelChange = () => {
    const enabled = !streamChannel;
    if (enabled) {
      buildStreamQueue(topology);
    }
    else {
      removeStreamQueue(topology);
    }
    onTopologyChange();
    setStreamChannel(enabled);
  };

  const handleRacPresetChange = () => {
    const enabled = !racPreset;
    if (enabled) {
      buildRacPreset(topology);
    }
    else {
      removeRacPreset(topology);
    }
    onTopologyChange();
    setRacPreset(enabled);
  };

  const handleSgdpPresetChange = () => {
    const enabled = !sgdpPreset;
    if (enabled) {
      buildSgdpPreset(topology);
    } else {
      removeSgdpPreset(topology);
    }
    onTopologyChange();
    setSgdpPreset(enabled);
  };

  const handleSmartTalkPresetChange = () => {
    const enabled = !smartTalkPreset;
    if (enabled) {
      buildSmartTalkPreset(topology, smartTalkApplicationId || '');
    } else {
      removeSmartTalkPreset(topology);
    }
    onTopologyChange();
    setSmartTalkPreset(enabled);
  };

  const handleSchedulerPresetChange = () => {
    const enabled = !schedulerPreset;
    if (enabled) {
      buildSchedulerPreset(topology);
    } else {
      removeSchedulerPreset(topology);
    }
    onTopologyChange();
    setSchedulerPreset(enabled);
  };

  const handleAccessRequestsPresetChange = () => {
    const enabled = !accessRequestsPreset;
    if (enabled) {
      buildAccessRequestsPreset(topology);
    } else {
      removeAccessRequestsPreset(topology);
    }
    onTopologyChange();
    setAccessRequestsPreset(enabled);
  };

  return (<>

    <Accordion expanded={expanded === 'advanced'} onChange={handleAccordionStateChange('advanced')}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>Advanced</AccordionSummary>
      <AccordionDetails>
        <Box className={classes.root}>
          <List dense className={classes.list}>
            <ListItem>
              <ListItemIcon> <SnoozeIcon /> </ListItemIcon>
              <ListItemText primary="Delayed channel" secondary="Enables the support to delay the messages delivery to the system." />
              <ListItemSecondaryAction>
                <Switch edge="end" checked={delayedChannel} onChange={handleDelayedChannelChange} />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemIcon> <MessagingQueueIcon /> </ListItemIcon>
              <ListItemText primary="Stream channel" secondary="Enables the support to stream the messages delivery to the system." />
              <ListItemSecondaryAction>
                <Switch edge="end" checked={streamChannel} onChange={handleStreamChannelChange} />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion expanded={expanded === 'presets'} onChange={handleAccordionStateChange('presets')}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>Presets</AccordionSummary>
      <AccordionDetails>

        <Box className={`${classes.root}`}>

          <Typography variant='caption' component="p">
            <b>Attention!</b>
            <br />
            The topology changes made here should not be changed manually to avoid undesired results.
          </Typography>

          <List dense className={`${classes.list}`}>
            <ListItem>
              <ListItemIcon> <ArrowRightAltIcon /> </ListItemIcon>
              <ListItemText primary="RAC" secondary="Enables the incoming messages of user, organizations and workspaces." />
              <ListItemSecondaryAction>
                <Switch edge="end" checked={racPreset} onChange={handleRacPresetChange} />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem disabled>
              <ListItemIcon> <SwapHorizIcon /> </ListItemIcon>
              <ListItemText primary="SGDP" secondary="Not supported yet" />
              <ListItemSecondaryAction>
                <Switch disabled edge="end" checked={sgdpPreset} onChange={handleSgdpPresetChange} />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem disabled={!smartTalkApplicationId}>
              <ListItemIcon> <SwapHorizIcon /> </ListItemIcon>
              <ListItemText primary="Smart Talk" secondary={smartTalkApplicationId
                ? "Enables the two-way communication only for messages addressed for this system."
                : "The system has no Smart Talk Application ID defined."
              } />
              <ListItemSecondaryAction>
                <Switch disabled={!smartTalkApplicationId} edge="end" checked={smartTalkPreset} onChange={handleSmartTalkPresetChange} />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemIcon> <SwapHorizIcon /> </ListItemIcon>
              <ListItemText primary="Scheduler" secondary="Enables the two-way communication only for messages addressed for this system." />
              <ListItemSecondaryAction>
                <Switch edge="end" checked={schedulerPreset} onChange={handleSchedulerPresetChange} />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemIcon> <SwapHorizIcon /> </ListItemIcon>
              <ListItemText primary="Access Requests" secondary="Enables the Access Request features." />
              <ListItemSecondaryAction>
                <Switch edge="end" checked={accessRequestsPreset} onChange={handleAccessRequestsPresetChange} />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Box>
      </AccordionDetails>
    </Accordion>
  </>);
}